<template>
  <div class="field" :class="fieldClassObject" v-show="!field.hidden">
    <label class="field__label" :class="labelClassObject" v-if="withLabel" :for="labelId">
      {{ field.label }}
      <span v-if="!field.optional" class="mandatory-mark">*</span>
    </label>
    <template v-if="!!$slots['custom-input']">
      <slot name="custom-input" />
    </template>
    <template v-else-if="field.type == 'select'">
      <div class="custom-select" :class="{ 'custom-select--loading': isLoading(field) }">
        <select
          class="field__input field__input--select"
          :class="{ 'is-placeholder': isPlaceholder(selectValue) }"
          ref="field"
          v-on="eventHandlers"
          :id="labelId"
          :name="field.key"
          :value="selectValue"
          @change="updateModel"
        >
          <option
            v-for="(option, index) in selectData[field.key]"
            :key="index"
            :value="option.value"
            >{{ option.text }}</option
          >
        </select>
      </div>
    </template>
    <template v-else>
      <input
        class="field__input field__input--text"
        ref="field"
        v-on="eventHandlers"
        :id="labelId"
        :name="field.key"
        :value="model"
        :placeholder="placeholder"
        :autocomplete="autocompleteSetting"
        :type="field.type"
        @input="updateModel"
      />
    </template>
    <div v-if="shouldShowErrorMessage" class="form-validation-error">
      <p v-for="(msg, errorIndex) in field.errors" :key="errorIndex">{{ msg }}</p>
    </div>
  </div>
</template>

<script>
/**
 * @file Field
 * @author pierre-michel@on-running.com
 *
 * Standardize the way we wrap and style our fields (input, select or custom slot). A field should
 * have a wrapper element class="field" for spacing between fields and should have a label tag or
 * placeholder. The field is followed by a div element where the specific errors related to it will
 * be displayed if needed.
 *
 * TODO: Currently support two different label styles: actual label tag or placeholder. We should
 * stick to one style only and remove that option.
 */
import { getHash } from '@/helpers/number'
import formUtilsMixin from '@/mixins/form-utils'

export default {
  name: 'Field',
  mixins: [formUtilsMixin],

  props: {
    field: {
      type: Object,
      required: true,
      validator(obj) {
        return ['key', 'type', 'label', 'errors', 'on'].every(k => typeof obj[k] != 'undefined')
      },
    },
    fieldsMarkedAsValid: {
      type: Object,
      default: () => {
        return {}
      },
    },
    model: { required: false },
    selectData: Object,
    labelStyle: {
      type: String,
      default: 'label',
      validator(value) {
        return ['label', 'placeholder'].indexOf(value) >= 0
      },
    },
    hideFieldErrors: Boolean,
    fieldClass: String,
    labelClass: String,
  },

  data() {
    return {
      hash: getHash(),
    }
  },

  computed: {
    selectValue() {
      if (this.labelStyle == 'label' || typeof this.model == 'undefined') {
        return this.model
      }
      return this.selectData[this.field.key].some(opt => opt.value == this.model)
        ? this.model
        : undefined
    },

    withLabel() {
      return this.labelStyle == 'label'
    },

    shouldShowErrorMessage() {
      return this.field.errors.length && (this.field.hasSpecificError || !this.hideFieldErrors)
    },

    labelId() {
      return this.withLabel ? `${this.field.key}-${this.hash}` : false
    },

    placeholder() {
      return this.labelStyle == 'placeholder' ? this.field.label : false
    },

    fieldClassObject() {
      const classObject = {
        'field--valid': !!this.fieldsMarkedAsValid[this.field.key],
        'field--error': this.field.errors.length > 0,
        'field--soft-validation': !this.hideFieldErrors,
      }
      classObject[this.fieldClass] = this.fieldClass
      return classObject
    },

    labelClassObject() {
      const classObject = {}
      classObject[this.labelClass] = this.labelClass
      return classObject
    },

    autocompleteSetting() {
      const turnOff =
        typeof this.field.autocomplete !== 'undefined' && this.field.autocomplete === false
      return turnOff ? this.field.key : false
    },

    eventHandlers() {
      const handlers = this.field.on ? { ...this.field.on } : {}
      delete handlers.change // handled in updateModel method

      return handlers
    },
  },

  methods: {
    updateModel(event) {
      const input = event.target
      const hasFocus = input === document.activeElement
      let value = event.target.value || undefined
      let selectionStart, selectionEnd

      if (this.field.formatter) {
        if (hasFocus) {
          selectionStart = input.selectionStart
          selectionEnd = input.selectionEnd
        }
        value = this.field.formatter(value)
      }
      this.$nextTick(() => {
        if (this.field.on && this.field.on.change) this.field.on.change(event)
        if (this.field.validateOnChange) this.field.validateOnChange(event)
      })
      this.$emit('update:model', value)
      this.$emit('on:sync-form-data', value)

      if (this.field.formatter && hasFocus) {
        this.$nextTick(() => {
          input.setSelectionRange(selectionStart, selectionEnd)
        })
      }
    },
  },
}
</script>
